// Parent component for the application tracker
// Cleaned up on 9/7

import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';

import { useAuth } from '../../contexts/AuthContext';
import TutorialTooltip from '../DailyDigest/components/TutorialTooltip';
import TutorialRestartButton from '../TutorialRestartButton';
import AddApplicationModal from './AddApplicationModal';
import ApplicationDetails from './ApplicationDetails';
import ApplicationTrackerTable from './ApplicationTrackerTable';
import AutoScraperInfoModal from './AutoScraperInfoModal';
import { TUTORIAL_STEPS } from './config/tutorialConfig';
import LoadingScrapingBanner from './LoadingScrapingBanner';
import LoginWithGoogleBanner from './LoginWithGoogleBanner';
import ScrapeEmailsModal from './ScrapeEmailsModal';

const AppTracker = () => {
  const { user } = useAuth();
  const { setCurrentApps } = useContext(ApplicationTrackerContext);
  const {
    creds,
    hasOnboarded,
    updateTutorialFlag,
    userProfile,
    userSchool,
    showToast,
    toastMessage,
    hideToast,
  } = useContext(GlobalContext);
  const {
    emailScrapingAllowed,
    refreshKey,
    removeEmailScrapingAccess,
    loadScraping,
    scrapeEmailsModalOpen,
    appDetailsOpen,
    addApplicationModalOpen,
    autoScraperInfoModalOpen,
  } = useContext(ApplicationTrackerContext);

  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);

  // Add refs for tutorial nodes
  const nodeRefs = {
    'tracker-table': useRef(null),
    'add-application': useRef(null),
    'email-access': useRef(null),
  };

  // Add effect to handle body scroll during tutorial

  useEffect(() => {
    if (userProfile && userProfile.onboarding_progress) {
      setShowTutorial(userProfile.onboarding_progress.hasSeenAppTrackerTutorial !== true);
    }
  }, [userProfile]);

  useEffect(() => {
    if (showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTutorial]);

  const handleNextStep = () => {
    setCurrentTutorialStep((prev) => prev + 1);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    updateTutorialFlag('hasSeenAppTrackerTutorial', true);
  };

  const TutorialOverlay = () => {
    if (!showTutorial) return null;

    const currentStep = TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = nodeRefs[currentStep.id];
    const rect = currentRef?.current?.getBoundingClientRect();

    // If rect is not available and we're on the last step, complete the tutorial
    if (!rect && currentTutorialStep === TUTORIAL_STEPS.length - 1) {
      handleTutorialComplete();
      return null;
    }

    if (!rect) return null;
    const getTooltipPosition = () => {
      const windowWidth = window.innerWidth;
      const isSmallScreen = windowWidth < 1024;

      if (currentStep.id === 'tracker-table') {
        const centeredLeft = rect.left + rect.width / 2 - 150;
        const boundedLeft = Math.max(10, Math.min(centeredLeft, windowWidth - 310)); // Ensure 10px margin

        return {
          top: rect.top - 20, // Position above content
          left: boundedLeft, // Center horizontally with bounds check
          transform: 'none',
          width: Math.min(300, rect.width) + 'px',
          '--arrow-orientation': 'down', // Arrow points down since tooltip is above
        };
      }

      if (currentStep.id === 'add-application') {
        const leftPosition = rect.left - 320;
        const boundedLeft = leftPosition < 0 ? rect.right + 20 : leftPosition; // Switch to right if would overflow left
        const arrowOrientation = leftPosition < 0 ? 'left' : 'right';

        return {
          top: rect.top + rect.height / 2 - 120,
          left: Math.max(10, boundedLeft), // Position tooltip with bounds check
          transform: 'none',
          width: '300px',
          '--arrow-orientation': arrowOrientation,
        };
      }
      if (currentStep.id === 'email-access') {
        const leftPosition = rect.left - 100;
        const boundedLeft = Math.max(10, Math.min(leftPosition, windowWidth - 410)); // Ensure tooltip fits

        return {
          top: rect.bottom + 20,
          left: boundedLeft, // Position tooltip with bounds check
          transform: 'none',
          width: '400px', // Increased width
          '--arrow-orientation': 'up', // Arrow points up since tooltip is below
        };
      }

      if (isSmallScreen) {
        return {
          top: rect.bottom + 20,
          left: Math.max(10, rect.left),
          transform: 'none',
          width: Math.min(300, rect.width, windowWidth - 20) + 'px',
        };
      }

      const leftPosition = currentStep.placement === 'left' ? rect.left - 320 : rect.right + 20;
      const boundedLeft = Math.max(10, Math.min(leftPosition, windowWidth - 310));

      return {
        top: rect.top + rect.height / 2 - 100,
        left: boundedLeft,
        width: '300px',
      };
    };

    return (
      <>
        <div className="fixed inset-0 z-50">
          {rect && (
            <div
              className="absolute bg-transparent"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                borderRadius: '0.5rem',
              }}
            />
          )}
        </div>

        <TutorialTooltip
          step={currentStep}
          steps={TUTORIAL_STEPS}
          onNext={handleNextStep}
          onComplete={handleTutorialComplete}
          style={getTooltipPosition()}
        />
      </>
    );
  };

  const updateTutorialVisibility = (apps) => {
    if (userProfile && userProfile.onboarding_progress) {
      const hasSeenTutorial = userProfile.onboarding_progress.hasSeenAppTrackerTutorial === true;
      // const hasApplications = apps && apps.length > 0;
      setShowTutorial(!hasSeenTutorial);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`/api/v1/application/user/${user.sub}/detailed`);
      const apps = response.data.applications;

      // If there are no applications, set empty array and update tutorial
      if (!apps || apps.length === 0) {
        setCurrentApps([]);
        updateTutorialVisibility([]);
        return;
      }

      // Transform the data to match the expected format
      const transformedApps = apps.map((app) => ({
        ...app,
        applicationID: app.application_id,
        history: [], // empty history if you didn't scrape it
      }));

      setCurrentApps(transformedApps);
      updateTutorialVisibility(transformedApps);
      console.log('Fetched current apps with useEffect:', transformedApps);
    } catch (error) {
      console.error('Failed to fetch applications:', error.response?.data?.detail || error.message);
      setCurrentApps([]);
      updateTutorialVisibility([]);
    }
  };

  useEffect(() => {
    console.log('RErendering this page');
    fetchApplications();
  }, [refreshKey]);

  return (
    <div>
      {loadScraping && <LoadingScrapingBanner />}
      {!emailScrapingAllowed && <LoginWithGoogleBanner ref={nodeRefs['email-access']} />}
      {autoScraperInfoModalOpen && <AutoScraperInfoModal />}
      <div className="my-0 flex justify-center items-center">
        {addApplicationModalOpen && <AddApplicationModal />}
        {scrapeEmailsModalOpen && <ScrapeEmailsModal />}
      </div>

      <div className="my-3 flex justify-center items-center">
        {appDetailsOpen && <ApplicationDetails />}
      </div>

      <div className="flex-1 px-3 flex flex-col items-center">
        <div
          ref={nodeRefs['tracker-table']}
          className="px-4 sm:px-6 lg:px-8 lg:pb-7 mx-auto w-full"
        >
          <ApplicationTrackerTable nodeRef={nodeRefs['add-application']} />
        </div>

        {emailScrapingAllowed && (
          <div className="flex justify-center mt-4">
            <button onClick={removeEmailScrapingAccess} className="text-red-400 px-2 py-1 rounded-sm">
              I want to remove email scraping access
            </button>
          </div>
        )}
      </div>

      {showTutorial && <TutorialOverlay />}
      <TutorialRestartButton tutorialKey="hasSeenAppTrackerTutorial" />
    </div>
  );
};

export default AppTracker;
